"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "../../../utils";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "../command/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../base/popover/popover";
import { useInsideDialog } from "../../base/dialog/dialog";
const ComboboxContext = /*#__PURE__*/ React.createContext(undefined);
function useComboboxContext() {
    const context = React.useContext(ComboboxContext);
    if (!context) {
        throw new Error("Combobox components must be used within a Combobox");
    }
    return context;
}
export function Combobox({ value, onValueChange, multiple = false, open: controlledOpen, onOpenChange, disabled = false, onClear, modal, children }) {
    // Modal only when genuinely inside a Dialog (true subtree containment, not the
    // app-global open-modal count) — mirrors Select. Explicit `modal` wins.
    const insideDialog = useInsideDialog();
    const isModal = modal ?? insideDialog;
    const [internalOpen, setInternalOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState("");
    const open = controlledOpen ?? internalOpen;
    const setOpen = React.useCallback((newOpen)=>{
        if (controlledOpen === undefined) {
            setInternalOpen(newOpen);
        }
        onOpenChange?.(newOpen);
    }, [
        controlledOpen,
        onOpenChange
    ]);
    // Stable noop function to prevent recreating on every render
    const noop = React.useCallback(()=>{}, []);
    const contextValue = React.useMemo(()=>({
            value,
            onValueChange: onValueChange ?? noop,
            multiple,
            open,
            setOpen,
            searchQuery,
            setSearchQuery,
            disabled,
            onClear
        }), [
        value,
        onValueChange,
        noop,
        multiple,
        open,
        setOpen,
        searchQuery,
        disabled,
        onClear
    ]);
    return /*#__PURE__*/ _jsx(ComboboxContext.Provider, {
        value: contextValue,
        children: /*#__PURE__*/ _jsx(Popover, {
            open: open,
            onOpenChange: setOpen,
            modal: isModal,
            children: children
        })
    });
}
Combobox.displayName = "Combobox";
export const ComboboxTrigger = /*#__PURE__*/ React.forwardRef(({ placeholder, className, children, allowClear, id }, ref)=>{
    const { value, onValueChange, multiple, open, disabled, onClear } = useComboboxContext();
    const handleClearAction = ()=>{
        if (onClear) {
            onClear();
        } else if (multiple) {
            onValueChange([]);
        } else {
            onValueChange(undefined);
        }
    };
    const handleClearClick = (e)=>{
        e.stopPropagation();
        handleClearAction();
    };
    const handleClearKeyDown = (e)=>{
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            handleClearAction();
        }
    };
    const hasValue = multiple ? Array.isArray(value) && value.length > 0 : value !== undefined && value !== "";
    return /*#__PURE__*/ _jsx(PopoverTrigger, {
        asChild: true,
        children: /*#__PURE__*/ _jsxs("button", {
            ref: ref,
            id: id,
            type: "button",
            role: "combobox",
            "aria-haspopup": "listbox",
            "aria-expanded": open,
            disabled: disabled,
            className: cn(// min-h (not h): single-line content still renders 40px tall, but
            // multi-line trigger content (e.g. wrapped selection tags) can grow.
            "flex min-h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50", // Muted when nothing renders — a value whose entity can't be
            // resolved falls back to the placeholder instead of a blank field.
            !children && "text-muted-foreground", className),
            children: [
                /*#__PURE__*/ _jsx("span", {
                    className: "flex-1 text-left truncate",
                    children: children || placeholder || null
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "ml-2 flex items-center gap-1 shrink-0",
                    children: [
                        allowClear && hasValue && !disabled && /*#__PURE__*/ _jsx("div", {
                            role: "button",
                            onClick: handleClearClick,
                            onKeyDown: handleClearKeyDown,
                            "aria-label": "Clear selection",
                            tabIndex: 0,
                            className: "inline-flex items-center justify-center rounded-xs focus:outline-hidden focus:ring-1 focus:ring-ring cursor-pointer",
                            children: /*#__PURE__*/ _jsx(X, {
                                className: "h-4 w-4 opacity-50 hover:opacity-100 transition-opacity"
                            })
                        }),
                        /*#__PURE__*/ _jsx(ChevronDown, {
                            className: "h-4 w-4 opacity-50"
                        })
                    ]
                })
            ]
        })
    });
});
ComboboxTrigger.displayName = "ComboboxTrigger";
/**
 * ComboboxContent - Popover content wrapper with Command component
 *
 * IMPORTANT: This component disables automatic filtering (shouldFilter={false})
 * to give you full control over the filtering logic. You MUST implement your
 * own filtering in the parent component.
 *
 * @example
 * ```tsx
 * function MyCombobox() {
 *   const [searchQuery, setSearchQuery] = useState('');
 *
 *   // Implement your own filtering logic
 *   const filteredItems = items.filter(item =>
 *     item.label.toLowerCase().includes(searchQuery.toLowerCase())
 *   );
 *
 *   return (
 *     <Combobox>
 *       <ComboboxTrigger>Select...</ComboboxTrigger>
 *       <ComboboxContent>
 *         <ComboboxInput
 *           value={searchQuery}
 *           onValueChange={setSearchQuery}
 *         />
 *         <ComboboxList>
 *           {filteredItems.map(item => (
 *             <ComboboxItem key={item.value} value={item.value}>
 *               {item.label}
 *             </ComboboxItem>
 *           ))}
 *         </ComboboxList>
 *       </ComboboxContent>
 *     </Combobox>
 *   );
 * }
 * ```
 *
 * See UserSelect and DepartmentSelect for real-world examples.
 */ export function ComboboxContent({ className, align = "start", side = "bottom", children }) {
    return /*#__PURE__*/ _jsx(PopoverContent, {
        className: cn("w-(--radix-popover-trigger-width) min-w-[200px] p-0", className),
        align: align,
        side: side,
        children: /*#__PURE__*/ _jsx(Command, {
            shouldFilter: false,
            children: children
        })
    });
}
ComboboxContent.displayName = "ComboboxContent";
export function ComboboxInput({ placeholder, className, value: controlledValue, onValueChange: controlledOnValueChange }) {
    const { searchQuery, setSearchQuery } = useComboboxContext();
    // Use controlled props if provided, otherwise use context
    const inputValue = controlledValue !== undefined ? controlledValue : searchQuery;
    const handleValueChange = controlledOnValueChange || setSearchQuery;
    return /*#__PURE__*/ _jsx(CommandInput, {
        placeholder: placeholder,
        value: inputValue,
        onValueChange: handleValueChange,
        className: className
    });
}
ComboboxInput.displayName = "ComboboxInput";
export function ComboboxEmpty({ className, children }) {
    return /*#__PURE__*/ _jsx(CommandEmpty, {
        className: className,
        children: children
    });
}
ComboboxEmpty.displayName = "ComboboxEmpty";
export function ComboboxGroup({ heading, className, children }) {
    return /*#__PURE__*/ _jsx(CommandGroup, {
        heading: heading,
        className: className,
        children: children
    });
}
ComboboxGroup.displayName = "ComboboxGroup";
export function ComboboxList({ className, children }) {
    return /*#__PURE__*/ _jsx(CommandList, {
        className: className,
        children: children
    });
}
ComboboxList.displayName = "ComboboxList";
export function ComboboxItem({ value: itemValue, disabled, className, children, onSelect }) {
    const { value, onValueChange, multiple, setOpen } = useComboboxContext();
    const isSelected = React.useMemo(()=>{
        if (multiple && Array.isArray(value)) {
            return value.includes(itemValue);
        }
        return value === itemValue;
    }, [
        value,
        itemValue,
        multiple
    ]);
    const handleSelect = React.useCallback(()=>{
        if (onSelect) {
            onSelect(itemValue);
            return;
        }
        if (multiple) {
            const currentValues = Array.isArray(value) ? value : [];
            const newValues = currentValues.includes(itemValue) ? currentValues.filter((v)=>v !== itemValue) : [
                ...currentValues,
                itemValue
            ];
            onValueChange(newValues);
        } else {
            onValueChange(itemValue);
            setOpen(false);
        }
    }, [
        itemValue,
        value,
        onValueChange,
        multiple,
        setOpen,
        onSelect
    ]);
    return /*#__PURE__*/ _jsxs(CommandItem, {
        value: itemValue,
        onSelect: handleSelect,
        disabled: disabled,
        className: cn(className),
        children: [
            typeof children === "string" || typeof children === "number" ? /*#__PURE__*/ _jsx("span", {
                className: cn("flex-1", isSelected && "font-semibold"),
                children: children
            }) : children,
            /*#__PURE__*/ _jsx(Check, {
                className: cn("h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")
            })
        ]
    });
}
ComboboxItem.displayName = "ComboboxItem";
export function ComboboxSeparator({ className }) {
    return /*#__PURE__*/ _jsx(CommandSeparator, {
        className: className
    });
}
ComboboxSeparator.displayName = "ComboboxSeparator";

//# sourceMappingURL=combobox.js.map